import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { accounts, EmailAccount, EmailMessage, messages, MessageStatus } from "../api/client";
import MessageStatusPill from "../components/MessageStatusPill";

const STATUS_OPTIONS: { value: "" | MessageStatus; label: string }[] = [
  { value: "", label: "All" },
  { value: "parsed", label: "Parsed into receipts" },
  { value: "skipped", label: "Skipped (not a receipt)" },
  { value: "pending", label: "Waiting for AI" },
  { value: "error", label: "Errors" },
];

export default function InboxActivity() {
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") ?? "") as "" | MessageStatus;
  const accountId = params.get("account_id") ?? "";
  const [rows, setRows] = useState<EmailMessage[]>([]);
  const [mailboxes, setMailboxes] = useState<EmailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      setRows(await messages.list({ status: status || undefined, account_id: accountId || undefined, limit: 200 }));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load activity");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, accountId]);

  useEffect(() => {
    accounts.list().then(setMailboxes).catch(() => {});
  }, []);

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  async function forceParse(m: EmailMessage) {
    try {
      await messages.forceParse(m.id);
      setRows((prev) => prev.map((r) => (r.id === m.id ? { ...r, status: "pending", status_reason: "queued" } : r)));
      setToast("Queued for AI parsing.");
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not queue");
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Inbox activity</h1>
        <p className="page-sub">
          Every email the worker fetched, and what happened to it. Anything skipped by mistake can be sent to the AI.
        </p>
      </div>

      <div className="filter-bar">
        <select value={status} onChange={(e) => setFilter("status", e.target.value)} aria-label="Status">
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select value={accountId} onChange={(e) => setFilter("account_id", e.target.value)} aria-label="Mailbox">
          <option value="">All mailboxes</option>
          {mailboxes.map((m) => (
            <option key={m.id} value={m.id}>{m.email_address}</option>
          ))}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={load}>Refresh</button>
      </div>

      {error && <div className="page-error">{error}</div>}
      {loading ? (
        <div className="page-empty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="page-empty">No emails here yet.</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Received</th>
              <th>From</th>
              <th>Subject</th>
              <th>Result</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.id}>
                <td style={{ whiteSpace: "nowrap" }}>{m.received_at ? new Date(m.received_at).toLocaleString() : "—"}</td>
                <td>{m.sender_name || m.sender_email || "—"}</td>
                <td style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.subject || "(no subject)"}
                </td>
                <td>
                  <MessageStatusPill status={m.status} />
                  {m.status === "parsed" && <span className="muted"> {m.receipt_count} receipt{m.receipt_count === 1 ? "" : "s"}</span>}
                  {m.status_reason && m.status_reason !== "force" && (
                    <div className="muted" style={{ fontSize: 12 }}>{m.status_reason}</div>
                  )}
                </td>
                <td style={{ textAlign: "right" }}>
                  {(m.status === "skipped" || m.status === "error") && (
                    <button className="btn btn-ghost btn-sm" onClick={() => forceParse(m)}>Parse with AI</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
